import {
  Calendar,
  Clock,
  Eye,
  FileText,
  Globe,
  Layout,
  MoveUpRight,
  Share2,
  Sparkles,
  Target,
  Upload,
} from "lucide-react";
import React from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { cn } from "../../utils/cn";
import { getPlatformConfig } from "../../utils/platformIcons";
import { ContentEntry } from "../cards/ContentCard";
import Button from "../ui/Button";
import Modal from "../ui/Modal";

interface PreviewContentModalProps {
  open: boolean;
  onClose: () => void;
  content: ContentEntry | null;
}

const PreviewContentModal: React.FC<PreviewContentModalProps> = ({
  open,
  onClose,
  content,
}) => {
  const { t } = useTranslation();
  if (!content) return null;

  const isUrl = content.type === "url";
  const isUrlLink = content.original_data?.startsWith("http");

  const getTypeConfig = (type: string) => {
    switch (type) {
      case "url":
        return {
          icon: <Globe />,
          color: "bg-blue-50 text-blue-700 border-blue-100",
          accent: "bg-blue-600",
          label: t("URL / Site"),
        };
      case "text":
        return {
          icon: <FileText />,
          color: "bg-emerald-50 text-emerald-700 border-emerald-100",
          accent: "bg-emerald-600",
          label: t("Conteúdo Textual"),
        };
      case "prompt":
        return {
          icon: <Sparkles />,
          color: "bg-purple-50 text-purple-700 border-purple-200",
          accent: "bg-purple-600",
          label: t("Prompt de IA"),
        };
      case "file":
        return {
          icon: <Upload />,
          color: "bg-orange-50 text-orange-700 border-orange-100",
          accent: "bg-orange-500",
          label: t("Arquivo Base"),
        };
      default:
        return {
          icon: <FileText />,
          color: "bg-gray-50 text-gray-700 border-purple-200",
          accent: "bg-gray-600",
          label: t("Outros"),
        };
    }
  };

  const typeConfig = getTypeConfig(content.type);

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeOnOverlayClick
      title={t("Detalhes do Conteúdo")}
      subtitle={t("Visualizando informações detalhadas de: {name}", {
        name: content.name,
      })}
      icon={<Eye className="h-5 w-5" />}
      headerVariant="gradient"
      maxWidth="max-w-4xl"
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button
            onClick={onClose}
            size="sm"
            variant="primary"
            className="px-8 shadow-lg shadow-purple-500/20"
          >
            {t("Entendido")}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Dashboard de Status e Metas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Tipo de Conteúdo */}
          <div className="p-4 bg-white border border-purple-200 rounded-3xl shadow-xs relative overflow-hidden group">
            <div
              className={cn(
                "absolute top-0 right-0 w-16 h-16 opacity-5 -mr-4 -mt-4 transition-transform duration-500 group-hover:scale-110",
                typeConfig.accent,
              )}
            >
              {React.cloneElement(typeConfig.icon as React.ReactElement<any>, {
                className: "w-full h-full",
              })}
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                {t("Classificação")}
              </span>
              <div
                className={cn(
                  "inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border font-bold text-xs w-max",
                  typeConfig.color,
                )}
              >
                {React.cloneElement(
                  typeConfig.icon as React.ReactElement<any>,
                  { className: "h-3.5 w-3.5" },
                )}
                {typeConfig.label}
              </div>
            </div>
          </div>

          {/* Data de Registro */}
          <div className="p-4 bg-white border border-purple-200 rounded-3xl shadow-xs space-y-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
              {t("Data de Entrada")}
            </span>
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-purple-50 rounded-xl text-purple-600">
                <Calendar className="h-4 w-4" />
              </div>
              <span className="text-gray-900 font-bold text-sm">
                {new Date(content.created_at).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>

          {/* Segmento de Negócio */}
          <div className="p-4 bg-white border border-purple-200 rounded-3xl shadow-xs space-y-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
              {t("Público Alvo")}
            </span>
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-indigo-50 rounded-xl text-indigo-600">
                <Target className="h-4 w-4" />
              </div>
              <span className="text-indigo-700 font-bold text-sm uppercase">
                {content.business_type || "B2B"}
              </span>
            </div>
          </div>

          {/* Status do Registro */}
          <div className="p-4 bg-white border border-purple-200 rounded-3xl shadow-xs space-y-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
              {t("Disponibilidade")}
            </span>
            <div className="flex items-center gap-2.5">
              <div
                className={cn(
                  "p-2 rounded-xl",
                  content.status === "active"
                    ? "bg-emerald-50 text-emerald-600"
                    : "bg-gray-100 text-gray-400",
                )}
              >
                <Clock className="h-4 w-4" />
              </div>
              <span
                className={cn(
                  "text-sm font-bold uppercase tracking-tighter",
                  content.status === "active"
                    ? "text-emerald-700"
                    : "text-gray-500",
                )}
              >
                {content.status === "active"
                  ? t("Ativo na Biblioteca")
                  : t("Arquivado")}
              </span>
            </div>
          </div>
        </div>

        {/* Canais de Distribuição */}
        {content.platforms && content.platforms.length > 0 && (
          <div className="p-6 bg-linear-to-br from-gray-50 to-white border border-purple-200 rounded-4xl space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2.5">
                <Share2 className="h-4 w-4 text-purple-500" />
                {t("Canais Habilitados para Geração")}
              </h4>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {content.platforms.map((platform, index) => {
                const platformConfig = getPlatformConfig(platform);
                const IconComponent = platformConfig.icon;
                return (
                  <div
                    key={index}
                    className="px-4 py-2.5 bg-white border border-purple-200 text-gray-700 rounded-2xl text-[11px] font-bold uppercase tracking-tight flex items-center gap-3 shadow-xs hover:shadow-md hover:border-purple-200 transition-all duration-300"
                  >
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <IconComponent className="h-4 w-4" />
                    </div>
                    {platformConfig.name}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Conteúdo Principal */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2.5">
              <Layout className="h-4 w-4 text-purple-500" />
              {t("Visualização do Conteúdo Base")}
            </h4>
          </div>

          <div className="relative rounded-4xl overflow-hidden border border-purple-200 bg-white shadow-xs overflow-hidden group">
            {isUrl && isUrlLink ? (
              <div className="p-8 space-y-6">
                <div className="p-6 bg-blue-50/30 rounded-3xl border border-blue-100 flex items-center justify-between group/link hover:bg-blue-50/50 transition-all duration-500">
                  <div className="flex items-center gap-5">
                    <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-500/20 group-hover/link:scale-110 transition-transform">
                      <Globe className="h-7 w-7" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold text-blue-700 uppercase tracking-widest mb-1.5 opacity-60">
                        {t("Fonte Externa")}
                      </p>
                      <h5 className="text-gray-900 font-bold text-lg sm:text-xl truncate break-all max-w-[250px] sm:max-w-none">
                        {content.original_data}
                      </h5>
                    </div>
                  </div>
                  <a
                    href={content.original_data}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-white text-blue-600 rounded-2xl border border-blue-100 shadow-xs hover:bg-blue-600 hover:text-white transition-all flex items-center gap-2 font-bold text-xs"
                  >
                    {t("Acessar Site")}
                    <MoveUpRight className="h-4 w-4" />
                  </a>
                </div>
                <p className="text-xs text-center text-gray-400 font-medium italic">
                  *{" "}
                  {t(
                    "O conteúdo deste site será analisado pela IA no momento da geração dos posts.",
                  )}
                </p>
              </div>
            ) : (
              <div className="flex flex-col">
                <div className="bg-gray-50/80 backdrop-blur-xs px-6 py-3 border-b border-purple-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-400/30"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/30"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-green-400/30"></div>
                    </div>
                    <span className="text-[10px] uppercase font-bold text-gray-400 tracking-[0.2em] ml-3">
                      {t("Biblioteca Digital")}
                    </span>
                  </div>
                </div>
                <div className="max-h-[500px] overflow-y-auto p-8 font-sans text-sm sm:text-base leading-relaxed text-gray-700 custom-scrollbar bg-white selection:bg-purple-100 selection:text-purple-900">
                  <pre className="whitespace-pre-wrap wrap-break-word font-sans selection:bg-purple-100">
                    {content.original_data}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PreviewContentModal;
